/**
 * CAPTCHA Component
 * 
 * Fetches and displays 6-digit alphanumeric CAPTCHA from backend
 * Implements backend-validated security requirement
 */

import React, { useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const CAPTCHA_LENGTH = 6;

/**
 * CAPTCHA Component
 * @param {Object} props
 * @param {string} props.value - Current CAPTCHA input value
 * @param {Function} props.onChange - Callback when CAPTCHA input changes
 * @param {string} props.error - Error message to display
 * @param {boolean} props.disabled - Whether the CAPTCHA is disabled
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onCaptchaChange - Callback when CAPTCHA key/data changes
 */
const Captcha = ({
  value = '',
  onChange,
  error = '',
  disabled = false,
  className = '',
  onCaptchaChange,
  refreshTrigger = 0
}) => {
  const [captchaData, setCaptchaData] = useState({ key: '', image: '' });
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Fetch CAPTCHA from backend
  const fetchCaptcha = useCallback(async () => {
    setIsRefreshing(true);
    setFetchError(null);
    try {
      const response = await authService.getCaptcha();
      if (response.success) {
        setCaptchaData(response.data);
        if (onCaptchaChange) {
          onCaptchaChange(response.data);
        }
        // Clear input when refreshing
        if (onChange) {
          onChange({ target: { value: '' } });
        }
      } else {
        setFetchError('Failed to load captcha');
      }
    } catch (err) {
      console.error('Error fetching captcha:', err);
      setFetchError('Failed to load captcha');
    } finally {
      setIsRefreshing(false);
    }
  }, [onChange, onCaptchaChange]);

  // Fetch initial CAPTCHA on mount
  useEffect(() => {
    fetchCaptcha();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh when trigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchCaptcha();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Handle input change
  const handleInputChange = (e) => {
    const inputValue = e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, CAPTCHA_LENGTH);
    if (onChange) {
      onChange({ target: { value: inputValue } });
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label htmlFor="captcha" className="block text-sm font-medium text-gray-700">
        Enter CAPTCHA <span className="text-red-500">*</span>
      </label>

      <div className="flex flex-col space-y-3">
        <div className="flex items-center space-x-3">
          {/* CAPTCHA Display */}
          <div className="flex-shrink-0">
            <div
              className={`
                relative bg-white 
                border-2 rounded-lg overflow-hidden
                select-none cursor-pointer
                transition-all duration-300
                min-w-[150px] h-[50px] flex items-center justify-center
                ${isRefreshing ? 'opacity-50 scale-95' : 'hover:shadow-md'}
                ${error || fetchError ? 'border-red-500' : 'border-gray-300'}
                `}
              onClick={fetchCaptcha}
              role="button"
              aria-label="Refresh CAPTCHA"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fetchCaptcha();
                }
              }}
            >
              {captchaData.image ? (
                <img
                  src={captchaData.image}
                  alt="Captcha"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-xs text-gray-400">Loading...</div>
              )}

              {/* Refresh icon overlay */}
              <div className="absolute top-1 right-1 bg-white/80 rounded-full p-0.5 opacity-30 hover:opacity-100 transition-opacity">
                <svg
                  className={`w-4 h-4 text-gray-600 ${isRefreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Input Field */}
          <div className="flex-1">
            <input
              id="captcha"
              name="captcha"
              type="text"
              value={value}
              onChange={handleInputChange}
              disabled={disabled || isRefreshing}
              autoComplete="off"
              maxLength={CAPTCHA_LENGTH}
              className={`
                w-full px-4 py-3 border rounded-lg 
                font-mono text-lg tracking-widest text-center
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                transition-colors duration-300
                ${error || fetchError ? 'border-red-500' : 'border-gray-300'}
                ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
                `}
              placeholder="Enter code"
              aria-describedby={error ? 'captcha-error' : undefined}
              aria-invalid={error ? 'true' : 'false'}
              aria-label="CAPTCHA verification code"
            />
          </div>
        </div>

        <button
          type="button"
          onClick={fetchCaptcha}
          className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-1"
          disabled={isRefreshing}
        >
          <svg className={`w-3 h-3 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Can't read? Refresh Captcha</span>
        </button>
      </div>

      {/* Error Message */}
      {(error || fetchError) && (
        <p id="captcha-error" className="text-sm text-red-600 mt-1" role="alert">
          {error || fetchError}
        </p>
      )}
    </div>
  );
};

/**
 * Validate CAPTCHA input (basic checks only, full validation is on backend)
 * @param {string} userInput - User's CAPTCHA input
 * @returns {Object} { isValid: boolean, error: string }
 */
export const validateCaptcha = (userInput) => {
  if (!userInput || !userInput.trim()) {
    return {
      isValid: false,
      error: 'CAPTCHA is required'
    };
  }

  if (userInput.length !== CAPTCHA_LENGTH) {
    return {
      isValid: false,
      error: `CAPTCHA must be ${CAPTCHA_LENGTH} characters`
    };
  }

  return {
    isValid: true,
    error: ''
  };
};

export default Captcha;


